import type { ReactNode } from 'react';
import { EngineKeyline } from './ui/primitives';
import { CoordinateMarks, TickRule } from './identity';

/* ----------------------------------------------------------------------------
 * <EngineDashboard> (signature surface, v1.1 §7) — the composed per-engine
 * briefing. The header is one of the three surfaces that carry the coordinate
 * marks (§2.7.2). It leads with the signature pairing: a tracked-mono engine
 * eyebrow directly above a serif headline (§2.2.1), the engine keyline as the
 * thin identity edge, then the hero metric in editorial/data type and a row of
 * AI insight cards. Everything tints via the ambient `data-engine` scope.
 * -------------------------------------------------------------------------- */
export function EngineDashboard({
  engineName,
  heroLabel,
  hero,
  heroSub,
  insights,
  children,
}: {
  engineName: string;
  heroLabel: string;
  hero: ReactNode; // hero metric — pass a <Score> to keep evidence-first
  heroSub?: string;
  insights?: ReactNode; // typically 3 <InsightCard>s
  children?: ReactNode;
}) {
  return (
    <section className="space-y-6">
      <header className="relative overflow-hidden rounded-md border border-border-subtle bg-surface">
        <EngineKeyline />
        <CoordinateMarks />
        <div className="p-6">
          {/* the signature pairing — tracked mono eyebrow + serif headline */}
          <div
            className="font-mono text-[11px] font-semibold uppercase leading-none text-engine"
            style={{ letterSpacing: '0.16em' }}
          >
            {engineName}
          </div>
          <div className="mt-2 font-display text-display-md font-semibold tracking-tight text-text-display">
            {heroLabel}
          </div>
          <TickRule className="mt-5" />
          <div className="mt-5 font-data text-[2.5rem] font-semibold leading-none tracking-tight text-text-display">
            {hero}
          </div>
          {heroSub && <div className="mt-2 text-body-md text-text-secondary">{heroSub}</div>}
        </div>
      </header>

      {insights && <div className="grid gap-3 md:grid-cols-3">{insights}</div>}
      {children}
    </section>
  );
}
